import { DEFAULT_DATE_FORMAT } from "../../../../configs/dateConfig";
import { useContext, useEffect, useState } from "react";
import { ProductOrder } from "../../../../data/entities";
import CustomTable from "../../../../shared/components/CustomTable";
import { StyledButton } from "../../../../shared/styled/TitleBar";
import { TableRow, Typography } from "@mui/material";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import IconButton from "../../../../shared/components/IconButton";
import { Delete, Edit } from "@mui/icons-material";
import { ContentWrapper, ProductButtonRow, ProductTableText } from "../../../ProductOrder/single/Styled";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import {
    ROLE,
    SALE_ORDER_STATUS,
    SALE_ORDER_TYPE,
} from "../../../../data/enums";
import apiMethods from "../../../../utils/api";
import { error, success } from "../../../../utils/toast";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { RequestImportProductModal } from "./RequestImportProductModal";
import { ConfirmModal } from "../../../../shared/components/ConfirmModal";
import { TitleText } from "../Styled";
import { NormalText } from "../../../../shared/components/ConfirmModal/Styled";
import { ImportProductModal } from "./ImportProductModal";
import Button from "../../../../shared/ui/shadcn/button";
import {
    canManageReserveImportRequest,
    getReserveImportActionMessage,
    getWorkshopForRole,
} from "../../../ReserveOrder/utils";

dayjs.extend(utc);

export const ImportRequestTableColumns = [
    { text: "Ngày đề nghị", icon: <></> },
    { text: "Tên sản phẩm", icon: <></> },
    { text: "Số lượng đề nghị", icon: <></> },
    { text: "Số lượng nhập", icon: <></> },
    { text: "Ghi chú", icon: <></> },
    { text: "Thao tác", icon: <></> },
];

interface ImportRequestRowProps {
    item: any;
    idx: number;
    extraRowProps: any;
}

export const ImportRequestRow = ({ item, extraRowProps }: ImportRequestRowProps) => {
    const formatDate = (date: string | Date): string => {
        if (!date) return "";
        return dayjs(date).utc().format(DEFAULT_DATE_FORMAT);
    };

    const isImported = item.is_imported;
    const product = item.products?.[0];

    return (
        <TableRow
            key={item.id}
            sx={{
                width: "100%",
            }}
        >
            <StyledTableCell align="left">
                <span>{formatDate(item.import_date || item.created_at) || "N/A"}</span>
            </StyledTableCell>
            <StyledTableCell align="left">
                {product?.product?.name || product?.product?.id}
            </StyledTableCell>
            <StyledTableCell align="left">
                {product?.required_quantity?.toString() ?? "0"}
            </StyledTableCell>
            <StyledTableCell align="left">{product?.quantity?.toString() ?? "0"}</StyledTableCell>
            <StyledTableCell align="left">{product?.note || ""}</StyledTableCell>
            <StyledTableCell align="left">
                <div style={{ display: "flex", gap: "10px" }}>
                    {extraRowProps.canManageRequest && (
                        <>
                            <IconButton
                                onClick={() => extraRowProps.removeItem(item)}
                                disabled={extraRowProps.disabledEdit || isImported}
                                sx={{ color: "red" }}
                            >
                                <Delete />
                            </IconButton>
                            <IconButton
                                onClick={() => extraRowProps.editItem(item)}
                                disabled={extraRowProps.disabledEdit || isImported}
                                sx={{ color: "#90caf9" }}
                            >
                                <Edit />
                            </IconButton>
                        </>
                    )}
                    {extraRowProps.canImport && (
                        <Button
                            onClick={() => extraRowProps.importProduct(item)}
                            title="Nhập kho"
                            size="sm"
                            className={`tw-ml-1 tw-h-1/3 ${
                                extraRowProps.disabledEdit || isImported
                                    ? "tw-bg-gray-500 tw-cursor-not-allowed hover:tw-bg-gray-500"
                                    : ""
                            }`}
                            disabled={extraRowProps.disabledEdit || isImported}
                        >
                            Nhập kho
                        </Button>
                    )}
                    {extraRowProps.importActionMessage && (
                        <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                        >
                            {extraRowProps.importActionMessage}
                        </Typography>
                    )}
                </div>
            </StyledTableCell>
        </TableRow>
    );
};

interface ImportRequestListProps {
    importRequests: any[];
    mergeImportRequest: Function;
    isView?: boolean;
    productOrder: ProductOrder;
    fetchProductOrder: () => Promise<void>;
    disabledEdit?: boolean;
    canImportReserve?: boolean;
}

const ImportRequestList = ({
                               importRequests,
                               mergeImportRequest,
                               isView,
                               productOrder,
                               fetchProductOrder,
                               disabledEdit,
                               canImportReserve = false,
                           }: ImportRequestListProps) => {
    const [isOpenRequestImportProductModal, setIsOpenRequestImportProductModal] = useState(false);
    const [isOpenImportProductModal, setIsOpenImportProductModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [targetRow, setTargetRow] = useState<any | null>(null);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [editInitialItems, setEditInitialItems] = useState<any[] | undefined>(undefined);
    const [importInitialItems, setImportInitialItems] = useState<any[] | undefined>(undefined);
    const [isEditingRequest, setIsEditingRequest] = useState(false);

    const onUpdateImportRequest = async (importData: any) => {
        try {
            if (!targetRow?.id) return;
            const p = (importData?.products || [])[0] || {};
            const payload: any = {
                required_quantity: Number(p?.required_quantity ?? 0),
                note: p?.note || "",
                receiver_name: importData?.receiver_name || "",
                import_date: importData?.import_date,
            };

            await apiMethods.put(
                `/product-flow/v2/request-import/${targetRow.id}/update/`,
                payload,
            );
            success("THÀNH CÔNG", "Cập nhật đề nghị nhập kho thành công");
            setIsOpenRequestImportProductModal(false);
            setEditInitialItems(undefined);
            setTargetRow(null);
            setIsEditingRequest(false);
            await fetchProductOrder();
        } catch (e) {
            error("Cập nhật đề nghị nhập kho thất bại", e as any);
        }
    };

    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const isReserve = productOrder.order_type === SALE_ORDER_TYPE.RESERVE;
    const isMatchingWorkshopManager =
        [
            ROLE.FACTORY_MANAGER_ONE,
            ROLE.FACTORY_MANAGER_TWO,
            ROLE.DEPUTY_FACTORY_MANAGER_ONE,
            ROLE.DEPUTY_FACTORY_MANAGER_TWO,
        ].includes(role) &&
        getWorkshopForRole(role) === productOrder.workshop;
    const canEdit = isReserve
        ? role === ROLE.MANAGER || isMatchingWorkshopManager
        : [
              ROLE.MANAGER,
              ROLE.FACTORY_MANAGER_ONE,
              ROLE.FACTORY_MANAGER_TWO,
              ROLE.DEPUTY_FACTORY_MANAGER_ONE,
              ROLE.DEPUTY_FACTORY_MANAGER_TWO,
          ].includes(role);
    const canManageRequest =
        canEdit &&
        (!isReserve ||
            canManageReserveImportRequest(String(productOrder.status)));
    const isReserveImportStage =
        String(productOrder.status) ===
        SALE_ORDER_STATUS.IMPORTING_AND_EXPORTING;
    const canImport = isReserve
        ? [ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER].includes(role) &&
          canImportReserve &&
          isReserveImportStage
        : [ROLE.FIRST_STORAGE_KEEPER, ROLE.SECOND_STORAGE_KEEPER, ROLE.MANAGER].includes(role);
    const importActionMessage = isReserve
        ? getReserveImportActionMessage(
              String(productOrder.status),
              canImport,
          )
        : "";

    const onAskRemoveImportRequest = (item: any) => {
        setTargetRow(item);
        setIsDeleting(true);
    };

    const onRemoveImportRequest = async (removeItem: any) => {
        if (!removeItem?.id) {
            setIsDeleting(false);
            setTargetRow(null);
            return;
        }
        setIsDeleteLoading(true);
        try {
            await apiMethods.delete(`/product-flow/v2/request-import/${removeItem.id}/delete`);
            success("THÀNH CÔNG", "Xóa đề nghị nhập kho thành công");
            await fetchProductOrder();
        } catch (ex: any) {
            error("Xóa đề nghị nhập kho thất bại", ex);
        } finally {
            setIsDeleteLoading(false);
            setIsDeleting(false);
            setTargetRow(null);
        }
    };

    const onUpdateImportRequestField = (id: string, field: string, value: any) => {
        const updatedImportRequests = importRequests.map((request) => {
            if (request.id === id) {
                return { ...request, [field]: value };
            }
            return request;
        });

        mergeImportRequest({ import_requests: updatedImportRequests });
    };

    const onEditImportRequest = (item: any) => {
        try {
            const product = (item?.products || [])[0];
            if (!product) {
                setEditInitialItems(undefined);
            } else {
                const prefill = {
                    id: product?.product?.id || product?.product_id || product?.id,
                    name: product?.product?.name || product?.name || "",
                    importQuantity: Number(product?.required_quantity || product?.quantity || 0),
                    note: product?.note || "",
                };
                setEditInitialItems([prefill]);
            }
            setTargetRow(item);
            setIsEditingRequest(true);
            setIsOpenRequestImportProductModal(true);
        } catch {
            setEditInitialItems(undefined);
            setIsOpenRequestImportProductModal(true);
        }
    };

    const onRequestImport = async (importRequest: any) => {
        try {
            await apiMethods.post(`/product-flow/v2/request-import/`, importRequest);
            setIsOpenRequestImportProductModal(false);
            success("THÀNH CÔNG", "Đề xuất nhập kho thành công");
            // Refresh the product order data to update the table
            await fetchProductOrder();
        } catch (ex) {
            console.error(`Failed to request import:`, ex);
            error("Nhập kho sản phẩm thất bại", ex);
        }
    };

    const onImportProduct = (item: any) => {
        setTargetRow(item);
        try {
            const p = (item?.products || [])[0] || {};
            const productId = p?.product?.id || p?.product_id || p?.id;
            const productName = p?.product?.name || p?.name || "";
            const required = Number(p?.required_quantity || 0);
            const imported = Number(p?.quantity || 0);
            const remaining = Math.max(required - imported, 0);
            const productCode = p?.product?.product_code || "";
            const prefill = {
                id: productId,
                product_code: productCode,
                name: productName,
                requiredQuantity: required,
                importQuantity: remaining || required || 0,
                note: p?.note || "",
            };
            setImportInitialItems([prefill]);
        } catch {
            setImportInitialItems(undefined);
        }
        setIsOpenImportProductModal(true);
    };

    const onSubmitImportProduct = async (importData: any) => {
        try {
            if (!targetRow?.id) return;
            const p = (importData?.products || [])[0] || {};
            const payload: any = {
                quantity: Number(p?.quantity ?? 0),
                note: p?.note || "",
                receiver_name: importData?.receiver_name || "",
                import_date: importData?.import_date,
            };
            await apiMethods.put(
                `/product-flow/v2/request-import/${targetRow.id}/update/`,
                payload,
            );
            await fetchProductOrder();
        } catch (e) {
            error("Nhập kho sản phẩm thất bại", e as any);
            throw e;
        }
    };

    return (
        <>
            {isDeleting && (
                <ConfirmModal
                    open={isDeleting}
                    handleClose={() => {
                        setIsDeleting(false);
                        setTargetRow(null);
                    }}
                    handleConfirm={() =>
                        !isDeleteLoading && targetRow && onRemoveImportRequest(targetRow)
                    }
                    confirmButtonText={isDeleteLoading ? "Đang xóa..." : "Xóa"}
                >
                    <TitleText>Xóa đề nghị nhập kho</TitleText>
                    <NormalText>Bạn có chắc chắn muốn xóa đề nghị nhập kho này không?</NormalText>
                </ConfirmModal>
            )}
            <ContentWrapper>
                {isReserve && (
                    <Typography
                        data-testid="reserve-import-guidance"
                        variant="body2"
                        color="text.secondary"
                        sx={{ marginBottom: 2 }}
                    >
                        Quản lý tạo đề nghị nhập kho. Khi đơn chuyển tới bước
                        Nhập kho, thủ kho được bàn giao sẽ thấy nút Nhập kho tại
                        cột Thao tác.
                    </Typography>
                )}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <ProductTableText style={{ width: "100%" }}>Danh sách đề nghị</ProductTableText>
                </div>

                <CustomTable
                    itemList={importRequests || []}
                    RowElement={ImportRequestRow}
                    TableColumns={ImportRequestTableColumns}
                    extraRowProps={{
                        productOrder: productOrder,
                        removeItem: onAskRemoveImportRequest,
                        editItem: onEditImportRequest,
                        updateItem: onUpdateImportRequestField,
                        importProduct: onImportProduct,
                        mergeImportRequest,
                        disabledEdit,
                        role,
                        canManageRequest,
                        canImport,
                        importActionMessage,
                    }}
                />

                {importRequests.length === 0 && "Không có yêu cầu nhập kho nào"}
                {!isView && canManageRequest && (
                    <ProductButtonRow>
                        <StyledButton
                            variant="contained"
                            onClick={() => {
                                setIsOpenRequestImportProductModal(true);
                            }}
                            // TODO: NT need to fix type
                            disabled={
                                disabledEdit ||
                                (isReserve &&
                                    productOrder.reserve_import_progress
                                        ?.is_fully_imported)
                            }
                        >
                            Thêm đề nghị
                        </StyledButton>
                    </ProductButtonRow>
                )}

                {isOpenRequestImportProductModal && (
                    <RequestImportProductModal
                        open={isOpenRequestImportProductModal}
                        handleClose={() => {
                            setIsOpenRequestImportProductModal(false);
                            setEditInitialItems(undefined);
                            setTargetRow(null);
                            setIsEditingRequest(false);
                        }}
                        onImport={(importData) => {
                            onRequestImport(importData);
                        }}
                        onUpdate={(importData) => {
                            onUpdateImportRequest(importData);
                        }}
                        saleOrder={productOrder}
                        initialFormData={{
                            customer: productOrder.customer?.id as any,
                            import_method: "nhập hàng sản xuất",
                            receiver_name: targetRow?.receiver_name || "",
                            import_date: targetRow?.import_date || targetRow?.created_at || new Date(),
                        }}
                        initialItems={editInitialItems}
                        isEditing={isEditingRequest}
                        isReserve={isReserve}
                    />
                )}

                {isOpenImportProductModal && (
                    <ImportProductModal
                        open={isOpenImportProductModal}
                        handleClose={() => {
                            setIsOpenImportProductModal(false);
                            setImportInitialItems(undefined);
                        }}
                        onImport={() => {
                            onImportProduct(targetRow);
                        }}
                        onSubmit={async (importData) => onSubmitImportProduct(importData)}
                        initialFormData={{
                            customer: productOrder.customer?.name as any,
                            import_method: "nhập hàng sản xuất",
                            receiver_name: targetRow?.receiver_name || "",
                            import_date: targetRow?.import_date || targetRow?.created_at || new Date(),
                        }}
                        initialItems={importInitialItems}
                        isReserve={isReserve}
                    />
                )}
            </ContentWrapper>
        </>
    );
};

interface Props {
    productOrder: ProductOrder;
    fetchProductOrder: () => Promise<void>;
    disabledEdit?: boolean;
    canImportReserve?: boolean;
}

const ImportRequest = ({
    productOrder,
    fetchProductOrder,
    disabledEdit,
    canImportReserve,
}: Props) => {
    const [importRequests, setImportRequests] = useState<any[]>(productOrder.import_requests || []);

    useEffect(() => {
        setImportRequests(productOrder.import_requests || []);
    }, [productOrder.import_requests]);

    const mergeImportRequest = (data: any) => {
        if (data.import_requests) {
            setImportRequests(data.import_requests);
        }
    };

    return (
        <ImportRequestList
            importRequests={importRequests}
            mergeImportRequest={mergeImportRequest}
            productOrder={productOrder}
            fetchProductOrder={fetchProductOrder}
            disabledEdit={disabledEdit}
            canImportReserve={canImportReserve}
        />
    );
};

export default ImportRequest;
